import { useMemo } from "react";

/**
 * Lightweight password-strength meter — no external dep (zxcvbn is 400KB+).
 *
 * Scoring rubric (0..4):
 *   0 = empty
 *   1 = weak         — <8 chars, or single character class
 *   2 = fair         — 8-11 chars + 2+ character classes
 *   3 = good         — 12+ chars + 3+ character classes
 *   4 = strong       — 14+ chars + 4 classes (lower, upper, digit, symbol) + no common patterns
 *
 * "Common patterns" penalises the top-10 leaked passwords (password, 123456, qwerty…) and
 * simple sequences (abcdef, 111111). Anything that trips those caps at score 2 even if long.
 *
 * The meter renders as a 4-segment bar + label + hint. Consumers can read `score` to enforce a
 * minimum before allowing form submission (recommended: `score >= 2` for admin accounts).
 */

const COMMON_PASSWORDS = new Set([
  "password", "12345678", "qwerty12", "letmein1", "welcome1",
  "admin123", "iloveyou", "monkey12", "abc12345", "111111aa",
]);

// Simple sequential-run detector — flags "abcdef", "12345", "aaaaaa" style stretches ≥5 chars.
function hasBoringSequence(pw) {
  if (/(.)\1{4,}/.test(pw)) return true; // 5+ repeats of same char
  for (let i = 0; i <= pw.length - 5; i++) {
    const chunk = pw.slice(i, i + 5);
    let seq = true;
    for (let j = 1; j < chunk.length; j++) {
      if (chunk.charCodeAt(j) !== chunk.charCodeAt(j - 1) + 1) { seq = false; break; }
    }
    if (seq) return true;
  }
  return false;
}

export function scorePassword(pw) {
  if (!pw) return { score: 0, label: "Empty", tips: ["Enter a password to check its strength."] };

  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length;
  const len = pw.length;
  const lowerPw = pw.toLowerCase();
  const isCommon = COMMON_PASSWORDS.has(lowerPw);
  const boring = hasBoringSequence(pw);

  const tips = [];
  let score = 0;

  if (isCommon) {
    tips.push("This is a very common password — pick something unique.");
    return { score: 1, label: "Weak", tips };
  }

  if (len < 8) {
    tips.push("Use at least 8 characters (12+ recommended).");
    score = 1;
  } else if (len < 12) {
    score = 2;
    tips.push("Add a few more characters — 12+ makes it much harder to guess.");
  } else if (len < 14) {
    score = 3;
  } else {
    score = 4;
  }

  if (classes < 2) {
    tips.push("Mix upper & lower case, numbers, and symbols.");
    score = Math.min(score, 1);
  } else if (classes < 3) {
    tips.push("Add another character type (symbol or number) for extra strength.");
    score = Math.min(score, 2);
  } else if (classes < 4 && score === 4) {
    score = 3;
  }

  if (boring) {
    tips.push("Avoid sequences like 'abcdef' or repeated characters.");
    score = Math.min(score, 2);
  }

  const labels = ["Empty", "Weak", "Fair", "Good", "Strong"];
  if (tips.length === 0) tips.push("Nice — this password is hard to guess.");
  return { score, label: labels[score], tips };
}

const BAR_COLORS = [
  "bg-elevated",       // 0
  "bg-red-500",        // 1 weak
  "bg-amber-400",      // 2 fair
  "bg-lime-400",       // 3 good
  "bg-emerald-400",    // 4 strong
];

const LABEL_COLORS = [
  "text-muted",
  "text-red-400",
  "text-amber-400",
  "text-lime-400",
  "text-emerald-400",
];

/**
 * Visual strength meter. Renders 4 segmented bars that fill left-to-right based on score, the
 * label, and one actionable tip. `testIdPrefix` scopes data-testids so multiple meters can live
 * on the same page (e.g., create + edit forms) without conflict.
 */
export function PasswordStrengthMeter({ password, testIdPrefix = "pw" }) {
  const { score, label, tips } = useMemo(() => scorePassword(password || ""), [password]);
  const segments = [1, 2, 3, 4];
  return (
    <div className="flex flex-col gap-1.5" data-testid={`${testIdPrefix}-strength`}>
      <div className="flex gap-1">
        {segments.map((seg) => (
          <div
            key={seg}
            className={`h-1.5 flex-1 rounded-full transition-colors ${seg <= score ? BAR_COLORS[score] : "bg-elevated"}`}
            data-testid={`${testIdPrefix}-strength-bar-${seg}`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className={`font-medium ${LABEL_COLORS[score]}`} data-testid={`${testIdPrefix}-strength-label`}>
          {label}
        </span>
        <span className="text-muted" data-testid={`${testIdPrefix}-strength-tip`}>
          {tips[0]}
        </span>
      </div>
    </div>
  );
}

export default PasswordStrengthMeter;
